// MainLayout.js
import React, { useState, useRef, useEffect } from 'react';
import { Outlet, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthProvider';
import Navigation from '../components/layout/Navigation';
import styled from 'styled-components';
import { MdMenu, MdClose, MdChevronLeft, MdChevronRight, MdPerson, MdSettings, MdLogout, MdKeyboardArrowDown } from 'react-icons/md';
import Logo from '../assets/images/dwlogo_wide.png';
import { Modal } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

const MainLayout = ({ NavigationComponent = Navigation }) => {
    const { logout, user } = useAuth();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [showNavModal, setShowNavModal] = useState(false);
    const [navCollapsed, setNavCollapsed] = useState(false);
    const [showProfileDropdown, setShowProfileDropdown] = useState(false);
    const dropdownRef = useRef(null);

    const handleLogout = () => {
        logout();
        navigate('/');
    };

    const toggleNavModal = () => {
        setShowNavModal(!showNavModal);
    };

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setShowProfileDropdown(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const getUserInitials = () => {
        if (!user?.name) return 'U';
        const names = user.name.split(' ');
        return names.length > 1
            ? `${names[0][0]}${names[1][0]}`.toUpperCase()
            : names[0][0].toUpperCase();
    };

    return (
        <LayoutContainer $collapsed={navCollapsed}>
            <TopBanner>
                <LogoContainer>
                    <img src={Logo} alt="Logo" />
                </LogoContainer>
                <TopBarRight>
                    <ProfileSection ref={dropdownRef}>
                        <ProfileButton onClick={() => setShowProfileDropdown(!showProfileDropdown)}>
                            <ProfileAvatar>{getUserInitials()}</ProfileAvatar>
                            <ProfileInfo>
                                <ProfileName>{user?.name || 'User'}</ProfileName>
                                <OrganizationName>{user?.organization?.name || ''}</OrganizationName>
                            </ProfileInfo>
                            <MdKeyboardArrowDown size={20} style={{ color: '#666', transform: showProfileDropdown ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
                        </ProfileButton>
                        {showProfileDropdown && (
                            <ProfileDropdown>
                                <DropdownItem as={Link} to="/profile" onClick={() => setShowProfileDropdown(false)}>
                                    <MdPerson size={18} />
                                    {t('Navigation.profile')}
                                </DropdownItem>
                                <DropdownItem as={Link} to="/settings" onClick={() => setShowProfileDropdown(false)}>
                                    <MdSettings size={18} />
                                    {t('Navigation.settings')}
                                </DropdownItem>
                                <DropdownDivider />
                                <DropdownItem onClick={handleLogout} $danger>
                                    <MdLogout size={18} />
                                    {t('Navigation.logout')}
                                </DropdownItem>
                            </ProfileDropdown>
                        )}
                    </ProfileSection>
                    <MobileMenuIcon onClick={toggleNavModal}>
                        <MdMenu size="24" />
                    </MobileMenuIcon>
                </TopBarRight>
            </TopBanner>

            <SidebarWrapper>
                <NavContainer>
                    <NavigationComponent collapsed={navCollapsed} />
                </NavContainer>
                <CollapseToggle type="button" onClick={() => setNavCollapsed((prev) => !prev)}>
                    {navCollapsed ? <MdChevronRight size={18} /> : <MdChevronLeft size={18} />}
                </CollapseToggle>
            </SidebarWrapper>

            <ContentWrapper>
                <Outlet />
            </ContentWrapper>

            <FullScreenModal show={showNavModal} onHide={toggleNavModal} centered>
                <ModalContent>
                    <CloseButton onClick={toggleNavModal}>
                        <MdClose size="24" />
                    </CloseButton>
                    <MobileProfileHeader>
                        <ProfileAvatar style={{ width: 48, height: 48, fontSize: 18 }}>{getUserInitials()}</ProfileAvatar>
                        <div>
                            <ProfileName style={{ fontSize: 16 }}>{user?.name || 'User'}</ProfileName>
                            <OrganizationName style={{ fontSize: 13 }}>{user?.organization?.name || ''}</OrganizationName>
                        </div>
                    </MobileProfileHeader>
                    <NavigationComponent
                        closeMenu={toggleNavModal}
                        collapsed={false}
                    />
                    <MobileMenuFooter>
                        <MobileMenuItem as={Link} to="/profile" onClick={toggleNavModal}>
                            <MdPerson size={20} />
                            {t('Navigation.profile')}
                        </MobileMenuItem>
                        <MobileMenuItem as={Link} to="/settings" onClick={toggleNavModal}>
                            <MdSettings size={20} />
                            {t('Navigation.settings')}
                        </MobileMenuItem>
                        <MobileMenuItem onClick={() => { toggleNavModal(); handleLogout(); }} $danger>
                            <MdLogout size={20} />
                            {t('Navigation.logout')}
                        </MobileMenuItem>
                    </MobileMenuFooter>
                </ModalContent>
            </FullScreenModal>
        </LayoutContainer>
    );
};

export default MainLayout;

// Styled Components for Layout
const LayoutContainer = styled.div`
    display: grid;
    /* minmax(0, 1fr), not 1fr: a bare 1fr track is minmax(auto, 1fr), so it
       cannot shrink below the tallest item's content. The sidebar carries 30+
       nav entries, which grew this row past the viewport — and since the
       container clips its overflow, the last few hundred pixels of every page
       became unreachable no matter how far you scrolled. Zooming out only
       hid it by making the nav fit again. */
    grid-template-rows: auto minmax(0, 1fr);
    grid-template-columns: ${({ $collapsed }) => ($collapsed ? '90px 1fr' : '260px 1fr')};
    height: 100vh;
    /* Mobile browsers count the collapsing address bar in 100vh; dvh is the
       height actually on screen, so a bottom button stays reachable there too. */
    height: 100dvh;
    overflow: hidden;
    background: #f5f5f5;

    @media (max-width: 1200px) {
        grid-template-columns: 200px 1fr;
    }

    @media (max-width: 768px) {
        grid-template-columns: 1fr;
    }
`;

const TopBanner = styled.header`
    grid-column: 1 / -1;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0.75rem 1.5rem;
    background: #ffffff;
    width: 100%;
    
    /* Restore the brand purple bottom border */
    border-bottom: 3px solid rgb(130, 22, 146);
    
    /* Optional: Keep a very subtle shadow if desired, but the border is the main separator now */
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.03);
    
    box-sizing: border-box;
    z-index: 10;

    @media (max-width: 1200px) {
        padding: 0.625rem 1.25rem;
    }

    @media (max-width: 768px) {
        padding: 0.625rem 1rem;
    }
`;

const LogoContainer = styled.div`
    display: flex;
    align-items: center;
    img {
        height: 40px;
    }

    @media (max-width: 1200px) {
        img {
            height: 30px;
        }
    }

    @media (max-width: 768px) {
        img {
            height: 25px;
        }
    }
`;

const TopBarRight = styled.div`
    display: flex;
    align-items: center;
    gap: 1rem;
`;

const ProfileSection = styled.div`
    position: relative;
`;

const ProfileButton = styled.button`
    display: flex;
    align-items: center;
    gap: 0.625rem;
    background: none;
    border: none;
    cursor: pointer;
    padding: 0.375rem 0.625rem;
    border-radius: 8px;
    transition: background 0.2s;

    &:hover {
        background: rgba(130, 22, 146, 0.05);
    }

    @media (max-width: 768px) {
        padding: 0.25rem;
        gap: 0.375rem;
    }
`;

const ProfileAvatar = styled.div`
    width: 36px;
    height: 36px;
    border-radius: 50%;
    background: linear-gradient(135deg, rgba(130, 22, 146, 0.8), rgba(130, 22, 146, 1));
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 600;
    font-size: 0.875rem;

    @media (max-width: 768px) {
        width: 32px;
        height: 32px;
        font-size: 0.75rem;
    }
`;

const ProfileInfo = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    text-align: left;

    @media (max-width: 900px) {
        display: none;
    }
`;

const ProfileName = styled.span`
    font-size: 0.8125rem;
    font-weight: 600;
    color: #333;
    line-height: 1.2;
`;

const OrganizationName = styled.span`
    font-size: 0.6875rem;
    color: #888;
    line-height: 1.2;
`;

const ProfileDropdown = styled.div`
    position: absolute;
    top: calc(100% + 8px);
    right: 0;
    background: white;
    border-radius: 10px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
    min-width: 180px;
    padding: 0.375rem;
    z-index: 1000;
    animation: fadeIn 0.15s ease;

    @keyframes fadeIn {
        from {
            opacity: 0;
            transform: translateY(-8px);
        }
        to {
            opacity: 1;
            transform: translateY(0);
        }
    }
`;

const DropdownItem = styled.button`
    display: flex;
    align-items: center;
    gap: 0.625rem;
    width: 100%;
    padding: 0.625rem 0.75rem;
    border: none;
    background: none;
    cursor: pointer;
    font-size: 0.875rem;
    color: ${({ $danger }) => $danger ? '#dc2626' : '#333'};
    border-radius: 6px;
    text-decoration: none;
    transition: background 0.15s;

    &:hover {
        background: ${({ $danger }) => $danger ? 'rgba(220, 38, 38, 0.08)' : 'rgba(130, 22, 146, 0.06)'};
    }

    svg {
        color: ${({ $danger }) => $danger ? '#dc2626' : '#666'};
    }
`;

const DropdownDivider = styled.div`
    height: 1px;
    background: #eee;
    margin: 0.25rem 0;
`;

const MobileMenuIcon = styled.div`
    display: none;
    cursor: pointer;
    color: rgb(130, 22, 146);

    @media (max-width: 768px) {
        display: block;
    }
`;

const SidebarWrapper = styled.div`
    position: relative;
    height: 100%;
    /* The nav scrolls inside NavContainer rather than sizing the grid row.
       Overflow stays visible on purpose — CollapseToggle overhangs the right
       edge by 12px and would be cut in half otherwise. */
    min-height: 0;

    @media (max-width: 768px) {
        display: none;
    }
`;

const NavContainer = styled.aside`
    overflow-y: auto;
    overflow-x: hidden;
    height: 100%;
    display: flex;
    flex-direction: column;
    
    /* Unify sidebar with white background */
    background: #ffffff;
    border-right: 1px solid #e5e7eb; /* Lighter gray border */
    
    width: 100%;
    box-sizing: border-box;
`;

const ContentWrapper = styled.main`
    overflow-y: auto;
    /* min-height: 0 so this pane can be shorter than its content and actually
       scroll; overscroll-behavior keeps a bounce at the end of a page from
       chaining into the document behind it. */
    min-height: 0;
    overscroll-behavior: contain;
    height: 100%;
    max-width: 100%;
    background-color: #fff;
    box-sizing: border-box;
    padding: 1.25rem 1.25rem 4.5rem;

    @media (max-width: 1200px) {
        padding: 0.9375rem 0.9375rem 4.5rem;
    }

    @media (max-width: 768px) {
        max-width: 100%;
        padding: 0.625rem 0.625rem 4.5rem;
    }
`;

const FullScreenModal = styled(Modal)`
    .modal-dialog {
        max-width: 100%;
        margin: 0;
    }
    .modal-content {
        height: 100vh;
        width: 100vw;
        border: none;
        border-radius: 0;
    }
`;

const ModalContent = styled.div`
    position: relative;
    padding: 1.25rem;
    height: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    background-color: #f4f4f4;
`;

const CloseButton = styled.div`
    position: absolute;
    top: 20px;
    right: 20px;
    cursor: pointer;
    color: gray;
`;

const MobileProfileHeader = styled.div`
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 1rem 0;
    margin-bottom: 1rem;
    border-bottom: 1px solid #eee;
    width: 100%;
`;

const MobileMenuFooter = styled.div`
    margin-top: auto;
    padding-top: 1rem;
    border-top: 1px solid #eee;
    width: 100%;
`;

const MobileMenuItem = styled.button`
    display: flex;
    align-items: center;
    gap: 0.75rem;
    width: 100%;
    padding: 1rem 0;
    border: none;
    background: none;
    cursor: pointer;
    font-size: 1rem;
    color: ${({ $danger }) => $danger ? '#dc2626' : '#333'};
    text-decoration: none;

    svg {
        color: ${({ $danger }) => $danger ? '#dc2626' : '#666'};
    }

    &:hover {
        color: ${({ $danger }) => $danger ? '#dc2626' : 'rgba(130, 22, 146, 1)'};
    }
`;

const CollapseToggle = styled.button`
    position: absolute;
    top: 50%;
    right: -12px;
    transform: translateY(-50%);
    border: none;
    background: #ffffff;
    border-radius: 50%;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
    width: 28px;
    height: 28px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    color: rgba(130, 22, 146, 1);
    z-index: 2;

    &:hover {
        background: rgba(130, 22, 146, 0.1);
    }

    @media (max-width: 1200px) {
        display: none;
    }
`;
